(() => {
  "use strict";

  class GlucoseCurveCalculator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      const root = this;

      "use strict";

      /* ============================ DATA ============================ */

      var MGDL_PER_MMOL = 18;

      var INSULINS = [
        {id:"vetsulin", name:"Vetsulin / Caninsulin (porcine lente)", conc:40,
          dog:{low:0.25, high:0.5, dur:[10,14]},
          cat:{low:0.25, high:0.5, dur:[8,12], flat:"1 unit per cat q12h under 4 kg, 2 units per cat q12h over 4 kg"},
          note:"Licensed for dogs and cats. Roll the vial gently and thoroughly before drawing, do not shake to froth. Duration is frequently too short in cats. U-40 syringes only.",
          start:"Dogs 0.25 U/kg q12h. Cats 1 to 2 units per cat q12h."},
        {id:"prozinc", name:"ProZinc (protamine zinc insulin, PZI)", conc:40,
          dog:{low:0.2, high:0.5, dur:[10,14]},
          cat:{low:0.2, high:0.3, dur:[10,14], flat:"1 to 2 units per cat q12h is a common practical start"},
          note:"Long acting, licensed for cats and now also dogs in several markets. Mix by gentle inversion. U-40 syringes only.",
          start:"Cats 0.2 to 0.3 U/kg q12h. Dogs 0.2 to 0.5 U/kg q12h."},
        {id:"glargine", name:"Glargine U-100 (Lantus and biosimilars)", conc:100,
          dog:{low:0.25, high:0.5, dur:[10,14]},
          cat:{low:0.25, high:0.5, dur:[12,24], flat:"1 to 2 units per cat q12h, or 0.25 U/kg q12h"},
          note:"Clear solution, never shake or dilute. Depot effect means the full result of a dose change can take one to two weeks to appear. Widely used in cats aiming for diabetic remission. U-100 syringes only.",
          start:"Cats 0.25 U/kg or 1 to 2 units per cat q12h. Dogs 0.25 to 0.5 U/kg q12h."},
        {id:"glargine300", name:"Glargine U-300 (Toujeo)", conc:300,
          dog:{low:0.1, high:0.3, dur:[12,24]},
          cat:{low:0.1, high:0.3, dur:[12,24], flat:"Very small volumes, dosing errors are easy"},
          note:"Three times the concentration of Lantus. High overdose risk in small patients. Only use with a clear protocol and a syringe the practice has verified. Not a routine first choice.",
          start:"Low, individualised. Confirm against a current reference before use."},
        {id:"detemir", name:"Detemir (Levemir)", conc:100,
          dog:{low:0.1, high:0.2, dur:[10,14], potent:true},
          cat:{low:0.2, high:0.4, dur:[12,20], flat:"About 1 unit per cat q12h is a common start"},
          note:"Markedly more potent in dogs than other insulins. Starting at a conventional 0.25 U/kg has caused severe hypoglycaemia. Start low and titrate slowly. U-100 syringes only.",
          start:"Dogs 0.1 U/kg q12h. Cats 0.2 to 0.4 U/kg or about 1 unit per cat q12h."},
        {id:"nph", name:"NPH / isophane (Humulin N, Novolin N)", conc:100,
          dog:{low:0.25, high:0.5, dur:[4,10]},
          cat:{low:0.25, high:0.5, dur:[3,8], poor:true},
          note:"Duration is often too short for q12h control in dogs and is usually inadequate in cats. If the curve shows a short duration, change the insulin rather than raising the dose. U-100 syringes only.",
          start:"Dogs 0.25 U/kg q12h. Not recommended as a first line in cats."},
        {id:"mix7030", name:"70/30 mix (NPH plus regular)", conc:100,
          dog:{low:0.25, high:0.5, dur:[4,10], poor:true},
          cat:{low:0.25, high:0.5, dur:[3,8], poor:true},
          note:"Biphasic action gives an early sharp drop then an early loss of effect. Unpredictable in dogs and cats and not recommended for routine maintenance. If this patient is on it, the priority is usually a change of insulin, not a dose change.",
          start:"Not recommended for routine maintenance in dogs or cats."},
        {id:"regular", name:"Regular / neutral (Humulin R, Actrapid)", conc:100,
          dog:{low:0.1, high:0.2, dur:[1,4], poor:true},
          cat:{low:0.1, high:0.2, dur:[1,4], poor:true},
          note:"Short acting insulin for hospitalised and ketoacidotic patients, given as a CRI or intermittently. Not a maintenance insulin. A 12 hour curve does not apply.",
          start:"Hospital protocols only."},
        {id:"degludec", name:"Degludec (Tresiba)", conc:100,
          dog:{low:0.1, high:0.3, dur:[16,24]},
          cat:{low:0.1, high:0.3, dur:[16,24]},
          note:"Ultra long acting, used off label in some cats. Steady state takes several days, so curves taken too early under-read the effect. Evidence base is limited.",
          start:"Low and individualised, off label."},
        {id:"other", name:"Other or compounded insulin", conc:100,
          dog:{low:null, high:null, dur:[10,14]},
          cat:{low:null, high:null, dur:[10,14]},
          note:"Enter the product name and confirm the concentration from the vial label before dosing.",
          start:"Confirm against the product label."}
      ];

      var TARGETS = {
        dog:{nadirLow:90, nadirHigh:150, dayLow:100, dayHigh:250, renal:200, ret:250},
        cat:{nadirLow:90, nadirHigh:160, dayLow:120, dayHigh:300, renal:265, ret:300}
      };

      var HYPO = 65, HYPO_SEVERE = 50;

      /* ============================ STATE ============================ */

      var unit = "mmol";              // display unit
      var rows = [];                  // [{time:"08:00", bg:<display value string>}]

      /* ============================ HELPERS ============================ */

      function $(id){ return root.querySelector("#" + id); }

      function toMg(v){ return unit === "mmol" ? v * MGDL_PER_MMOL : v; }
      function fromMg(v){ return unit === "mmol" ? v / MGDL_PER_MMOL : v; }
      function fmt(mg, dp){
        if (mg === null || mg === undefined || isNaN(mg)) return "—";
        var v = fromMg(mg);
        var d = (dp === undefined) ? (unit === "mmol" ? 1 : 0) : dp;
        return v.toFixed(d);
      }
      function unitLabel(){ return unit === "mmol" ? "mmol/L" : "mg/dL"; }
      function both(mg){
        var mm = (mg/MGDL_PER_MMOL).toFixed(1), md = Math.round(mg);
        return unit === "mmol" ? (mm + " mmol/L (" + md + " mg/dL)") : (md + " mg/dL (" + mm + " mmol/L)");
      }

      function t2m(s){
        if (!s) return null;
        var p = s.split(":");
        if (p.length < 2) return null;
        var h = parseInt(p[0],10), m = parseInt(p[1],10);
        if (isNaN(h) || isNaN(m)) return null;
        return h*60 + m;
      }
      function m2t(m){
        m = ((m % 1440) + 1440) % 1440;
        var h = Math.floor(m/60), mm = m % 60;
        return String(h).padStart(2,"0") + ":" + String(mm).padStart(2,"0");
      }
      // place a clock time on the timeline that starts at startMin
      function place(clockMin, startMin){
        if (clockMin === null) return null;
        var d = clockMin - startMin;
        while (d < 0) d += 1440;
        return startMin + d;
      }
      function hrs(mins){
        var h = mins/60;
        return (Math.abs(h - Math.round(h)) < 0.02) ? String(Math.round(h)) : h.toFixed(1);
      }
      function round05(x){ return Math.round(x*2)/2; }
      function esc(s){ return String(s).replace(/[&<>"]/g, function(c){ return {"&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;"}[c]; }); }

      function weightKg(){
        var w = parseFloat($("weight").value);
        if (isNaN(w) || w <= 0) return null;
        return $("wunit").value === "lb" ? w * 0.4535924 : w;
      }
      function sp(){ return $("species").value; }
      function ins(){
        var id = $("insulin").value;
        for (var i=0;i<INSULINS.length;i++){ if (INSULINS[i].id === id) return INSULINS[i]; }
        return INSULINS[0];
      }
      function insConc(){
        var I = ins();
        return I.id === "other" ? parseInt($("customconc").value,10) : I.conc;
      }
      function insName(){
        var I = ins();
        if (I.id === "other"){
          var n = $("customname").value.trim();
          return n ? n + " (U-" + insConc() + ")" : "Custom insulin (U-" + insConc() + ")";
        }
        return I.name;
      }

      /* ============================ SETUP ============================ */

      function buildInsulinSelect(){
        var s = $("insulin");
        s.innerHTML = "";
        INSULINS.forEach(function(I){
          var o = document.createElement("option");
          o.value = I.id; o.textContent = I.name;
          s.appendChild(o);
        });
        s.value = "vetsulin";
      }

      function buildRefTable(){
        var b = $("reftbody");
        b.innerHTML = INSULINS.filter(function(I){ return I.id !== "other"; }).map(function(I){
          var dd = I.dog.dur, cd = I.cat.dur;
          return "<tr><td><b>" + esc(I.name.split(" (")[0]) + "</b></td>" +
            "<td>U-" + I.conc + "</td>" +
            "<td>" + esc(I.start) + "</td>" +
            "<td>Dog " + dd[0] + " to " + dd[1] + " h<br>Cat " + cd[0] + " to " + cd[1] + " h</td></tr>";
        }).join("");
      }

      /* ============================ SAMPLE ROWS ============================ */

      function renderRows(){
        var tb = $("samples");
        var startMin = t2m($("gstart").value);
        var injMin = t2m($("inj1").value);
        tb.innerHTML = "";
        rows.forEach(function(r, i){
          var tr = document.createElement("tr");
          var post = "—";
          if (r.time && injMin !== null){
            var tm = place(t2m(r.time), injMin);
            if (tm !== null) post = hrs(tm - injMin) + " h";
          }
          tr.innerHTML =
            "<td>" + (i+1) + "</td>" +
            "<td><input type='time' data-i='" + i + "' data-f='time' value='" + (r.time||"") + "'></td>" +
            "<td><input type='number' step='0.1' min='0' data-i='" + i + "' data-f='bg' value='" + (r.bg||"") + "' placeholder='0'></td>" +
            "<td style='color:#6B6878;font-size:13px'>" + post + "</td>" +
            "<td><button class='rm noprint' data-rm='" + i + "' title='Remove'>&times;</button></td>";
          tb.appendChild(tr);
        });
        tb.querySelectorAll("input").forEach(function(el){
          el.addEventListener("input", function(){
            rows[+this.dataset.i][this.dataset.f] = this.value;
            if (this.dataset.f === "time") renderRows();
            recalc();
          });
        });
        tb.querySelectorAll("[data-rm]").forEach(function(b){
          b.addEventListener("click", function(){
            rows.splice(+this.dataset.rm, 1);
            if (!rows.length) rows.push({time:"",bg:""});
            renderRows(); recalc();
          });
        });
      }

      function fillTimes(stepH){
        var startMin = t2m($("gstart").value);
        var endMin = t2m($("gend").value);
        if (startMin === null || endMin === null) return;
        var end = place(endMin, startMin);
        if (end === startMin) end = startMin + 1440;
        var span = end - startMin;
        var n = Math.floor(span / (stepH*60)) + 1;
        if (n > 40) n = 40;
        var old = rows.slice();
        rows = [];
        for (var i=0;i<n;i++){
          var t = m2t(startMin + i*stepH*60);
          var prev = old.filter(function(r){ return r.time === t; })[0];
          rows.push({time:t, bg: prev ? prev.bg : ""});
        }
        renderRows(); recalc();
      }

      /* ============================ CURVE MATHS ============================ */

      function series(){
        var startMin = t2m($("gstart").value);
        if (startMin === null) return [];
        var out = [];
        rows.forEach(function(r){
          var cm = t2m(r.time);
          var v = parseFloat(r.bg);
          if (cm === null || isNaN(v) || v <= 0) return;
          out.push({ t: place(cm, startMin), clock: r.time, mg: toMg(v) });
        });
        out.sort(function(a,b){ return a.t - b.t; });
        // remove duplicate timestamps, keep last
        var clean = [];
        out.forEach(function(p){
          if (clean.length && clean[clean.length-1].t === p.t) clean[clean.length-1] = p;
          else clean.push(p);
        });
        return clean;
      }

      function interpAt(pts, t){
        if (!pts.length) return null;
        if (t <= pts[0].t) return pts[0].mg;
        if (t >= pts[pts.length-1].t) return pts[pts.length-1].mg;
        for (var i=1;i<pts.length;i++){
          if (pts[i].t >= t){
            var a = pts[i-1], b = pts[i];
            var f = (t - a.t) / (b.t - a.t);
            return a.mg + f*(b.mg - a.mg);
          }
        }
        return null;
      }
      // first time at/after tFrom where the interpolated curve crosses up through `level`
      function crossUp(pts, level, tFrom){
        for (var i=1;i<pts.length;i++){
          var a = pts[i-1], b = pts[i];
          if (b.t <= tFrom) continue;
          var aT = Math.max(a.t, tFrom);
          var aV = interpAt(pts, aT);
          if (aV < level && b.mg >= level){
            var f = (level - aV) / (b.mg - aV);
            return aT + f*(b.t - aT);
          }
        }
        return null;
      }

      function analyse(){
        var pts = series();
        var T = TARGETS[sp()];
        var startMin = t2m($("gstart").value);
        var injMin = startMin === null ? null : place(t2m($("inj1").value), startMin);
        var A = { pts:pts, T:T, ok:false };
        if (pts.length < 2) return A;

        var nadir = pts[0], peak = pts[0];
        pts.forEach(function(p){
          if (p.mg < nadir.mg) nadir = p;
          if (p.mg > peak.mg) peak = p;
        });
        A.nadir = nadir; A.peak = peak;
        A.diff = peak.mg - nadir.mg;
        A.first = pts[0]; A.last = pts[pts.length-1];
        A.span = A.last.t - A.first.t;
        A.n = pts.length;

        // mean by trapezoid over sampled span
        var area = 0;
        for (var i=1;i<pts.length;i++){
          area += (pts[i].mg + pts[i-1].mg)/2 * (pts[i].t - pts[i-1].t);
        }
        A.mean = A.span > 0 ? area / A.span : nadir.mg;

        // time in range (1 minute resolution)
        var inR = 0, above = 0, below = 0, total = 0;
        for (var t = pts[0].t; t <= pts[pts.length-1].t; t++){
          var v = interpAt(pts, t);
          total++;
          if (v < T.dayLow) below++;
          else if (v > T.dayHigh) above++;
          else inR++;
        }
        A.pctIn = total ? inR/total*100 : 0;
        A.pctAbove = total ? above/total*100 : 0;
        A.pctBelow = total ? below/total*100 : 0;
        A.total = total;

        // time to nadir and duration of effect
        // durMode: "none"    = nadir never fell below the return threshold, duration is not measurable
        //          "unreliable" = insulin worked but the nadir stayed above target, so duration understates the truth
        //          "ok"      = measurable and interpretable
        if (injMin !== null){
          A.injMin = injMin;
          A.toNadir = nadir.t - injMin;
          if (nadir.mg >= T.ret){
            A.durMode = "none";
          } else {
            var back = crossUp(pts, T.ret, nadir.t);
            if (back !== null){ A.duration = back - injMin; A.durationCensored = false; A.backAt = back; }
            else { A.duration = A.last.t - injMin; A.durationCensored = true; }
            A.durMode = (nadir.mg > T.nadirHigh) ? "unreliable" : "ok";
          }
        }

        // rebound assessment
        var postMax = null;
        pts.forEach(function(p){ if (p.t > nadir.t && (postMax === null || p.mg > postMax.mg)) postMax = p; });
        A.postMax = postMax;
        A.rebound = (postMax && nadir.mg < 80 && (postMax.mg - nadir.mg) >= 160 && (postMax.t - nadir.t) <= 360);

        // still falling at the end?
        A.stillFalling = (pts.length >= 2) &&
          (A.last.mg < pts[pts.length-2].mg) &&
          (A.last.t === nadir.t) &&
          ((pts[pts.length-2].mg - A.last.mg) > 27);

        // sampling quality
        var gaps = [];
        for (var j=1;j<pts.length;j++) gaps.push(pts[j].t - pts[j-1].t);
        A.maxGap = gaps.length ? Math.max.apply(null, gaps) : 0;

        A.ok = true;
        return A;
      }

      /* ============================ CHART ============================ */

      function drawChart(A){
        var svg = $("chart");
        var W = 940, H = 470, padL = 66, padR = 26, padT = 22, padB = 58;
        var pw = W - padL - padR, ph = H - padT - padB;
        var T = TARGETS[sp()];
        var startMin = t2m($("gstart").value);
        var endMinRaw = t2m($("gend").value);
        var e = [];

        function el(tag, attrs, text){
          var s = "<" + tag;
          for (var k in attrs) s += " " + k + "='" + attrs[k] + "'";
          s += ">";
          if (text !== undefined) s += text;
          s += "</" + tag + ">";
          return s;
        }

        if (startMin === null || endMinRaw === null){
          svg.innerHTML = el("text",{x:W/2,y:H/2,"text-anchor":"middle",fill:"#6B6878","font-size":"15"},"Set a graph start and end time");
          return;
        }
        var endMin = place(endMinRaw, startMin);
        if (endMin <= startMin) endMin = startMin + 1440;
        var span = endMin - startMin;

        // y scale
        var pts = A.pts || [];
        var maxV = 300;
        pts.forEach(function(p){ if (p.mg > maxV) maxV = p.mg; });
        maxV = Math.max(maxV * 1.12, T.dayHigh * 1.15);
        var yMaxDisp, step;
        if (unit === "mmol"){
          var mm = maxV / MGDL_PER_MMOL;
          step = mm > 40 ? 10 : (mm > 20 ? 5 : 2);
          yMaxDisp = Math.ceil(mm/step)*step;
        } else {
          step = maxV > 700 ? 200 : (maxV > 350 ? 100 : 50);
          yMaxDisp = Math.ceil(maxV/step)*step;
        }
        var yMaxMg = unit === "mmol" ? yMaxDisp*MGDL_PER_MMOL : yMaxDisp;

        function X(t){ return padL + (t - startMin)/span * pw; }
        function Y(mg){ return padT + (1 - Math.max(0, Math.min(mg, yMaxMg))/yMaxMg) * ph; }

        // background bands
        e.push(el("rect",{x:padL,y:padT,width:pw,height:ph,fill:"#FFFFFF",stroke:"#E6E4EC"}));
        e.push(el("rect",{x:padL,y:Y(T.dayHigh),width:pw,height:Math.max(0,Y(T.dayLow)-Y(T.dayHigh)),fill:"#E8F6EF"}));
        e.push(el("rect",{x:padL,y:Y(HYPO),width:pw,height:Math.max(0,ph+padT-Y(HYPO)),fill:"#FBEBEA"}));

        // y gridlines
        for (var v=0; v<=yMaxDisp+0.001; v+=step){
          var mg = unit === "mmol" ? v*MGDL_PER_MMOL : v;
          var y = Y(mg);
          e.push(el("line",{x1:padL,y1:y,x2:padL+pw,y2:y,stroke:"#EFEDF4","stroke-width":1}));
          e.push(el("text",{x:padL-10,y:y+4,"text-anchor":"end",fill:"#6B6878","font-size":"11.5"}, String(v)));
        }
        e.push(el("text",{x:16,y:padT+ph/2,"text-anchor":"middle",fill:"#6B6878","font-size":"12","font-weight":"700",transform:"rotate(-90 16 " + (padT+ph/2) + ")"}, "Blood glucose (" + unitLabel() + ")"));

        // x gridlines
        var stepH = span/60 > 16 ? 2 : (span/60 > 8 ? 2 : 1);
        for (var t = startMin; t <= endMin + 1; t += stepH*60){
          var x = X(t);
          e.push(el("line",{x1:x,y1:padT,x2:x,y2:padT+ph,stroke:"#EFEDF4","stroke-width":1}));
          e.push(el("text",{x:x,y:padT+ph+18,"text-anchor":"middle",fill:"#6B6878","font-size":"11.5"}, m2t(t)));
          e.push(el("text",{x:x,y:padT+ph+33,"text-anchor":"middle",fill:"#A9A6B5","font-size":"10.5"}, (A.injMin !== undefined && A.injMin !== null) ? (hrs(t - A.injMin) + "h") : ""));
        }
        e.push(el("text",{x:padL+pw/2,y:H-8,"text-anchor":"middle",fill:"#6B6878","font-size":"12","font-weight":"700"}, "Time of day (hours after insulin)"));

        // renal threshold
        var yr = Y(T.renal);
        e.push(el("line",{x1:padL,y1:yr,x2:padL+pw,y2:yr,stroke:"#9A97A8","stroke-width":1.4,"stroke-dasharray":"2 4"}));
        e.push(el("text",{x:padL+pw-6,y:yr-6,"text-anchor":"end",fill:"#9A97A8","font-size":"10.5"},"Renal threshold"));

        // markers (feeds drawn first so insulin lines sit on top when they coincide)
        var marks = [];
        function addMark(clock, color, label, prio){
          var cm = t2m(clock);
          if (cm === null) return;
          var tm = place(cm, startMin);
          if (tm > endMin + 1) return;
          marks.push({ x: X(tm), color:color, label:label, prio:prio });
        }
        addMark($("feed1").value, "#B8790B", "Feed", 1);
        addMark($("feed2").value, "#B8790B", "Feed", 1);
        addMark($("inj1").value, "#8637B2", "Insulin", 2);
        addMark($("inj2").value, "#8637B2", "Insulin due", 2);
        marks.sort(function(a,b){ return a.prio - b.prio; });
        var used = {};   // stack labels that share an x position
        marks.forEach(function(m){
          e.push(el("line",{x1:m.x.toFixed(1),y1:padT,x2:m.x.toFixed(1),y2:padT+ph,stroke:m.color,"stroke-width":1.6,"stroke-dasharray":"5 4"}));
          var key = Math.round(m.x/14);
          var slot = used[key] || 0; used[key] = slot + 1;
          var nearRight = m.x > padL + pw - 74;
          e.push(el("text",{
            x: (nearRight ? m.x - 5 : m.x + 5).toFixed(1),
            y: padT + 13 + slot*14,
            "text-anchor": nearRight ? "end" : "start",
            fill:m.color,"font-size":"10.5","font-weight":"700"
          }, m.label));
        });

        // curve
        if (pts.length){
          var d = pts.map(function(p,i){ return (i?"L":"M") + X(p.t).toFixed(1) + " " + Y(p.mg).toFixed(1); }).join(" ");
          if (pts.length > 1){
            var fill = d + " L " + X(pts[pts.length-1].t).toFixed(1) + " " + (padT+ph) + " L " + X(pts[0].t).toFixed(1) + " " + (padT+ph) + " Z";
            e.push(el("path",{d:fill,fill:"#8637B2","fill-opacity":"0.04"}));
            e.push(el("path",{d:d,fill:"none",stroke:"#8637B2","stroke-width":"2.6","stroke-linejoin":"round","stroke-linecap":"round"}));
          }
          pts.forEach(function(p){
            var isN = A.nadir && p.t === A.nadir.t;
            var isP = A.peak && p.t === A.peak.t && !isN;
            var c = isN ? "#1E9E62" : (p.mg < HYPO ? "#C3352B" : "#8637B2");
            e.push(el("circle",{cx:X(p.t).toFixed(1),cy:Y(p.mg).toFixed(1),r: isN?6:4.5, fill:"#fff", stroke:c,"stroke-width":"2.6"},
              el("title",{}, p.clock + "  " + fmt(p.mg) + " " + unitLabel())));
            if (isN || isP){
              var lx = X(p.t), anc = "middle";
              if (lx < padL + 40){ lx = padL + 4; anc = "start"; }
              else if (lx > padL + pw - 40){ lx = padL + pw - 4; anc = "end"; }
              e.push(el("text",{x:lx.toFixed(1),y:(Y(p.mg) - (isN?16:14)).toFixed(1),"text-anchor":anc,fill:c,"font-size":"11.5","font-weight":"800"},
                (isN?"Nadir ":"Peak ") + fmt(p.mg)));
            }
          });
        } else {
          e.push(el("text",{x:padL+pw/2,y:padT+ph/2,"text-anchor":"middle",fill:"#A9A6B5","font-size":"14"},"Enter at least two sample times and glucose values"));
        }

        svg.innerHTML = e.join("");
      }

      /* ============================ INTERPRETATION ============================ */

      function doseInfo(){
        var I = ins(), s = sp(), sd = I[s], w = weightKg(), d = parseFloat($("dose").value);
        var out = { I:I, sd:sd, w:w, dose:isNaN(d)?null:d, perKg:null, status:null };
        if (out.dose !== null && w) out.perKg = out.dose / w;
        if (out.perKg !== null && sd && sd.low !== null){
          if (out.perKg < sd.low*0.8) out.status = "below";
          else if (out.perKg > sd.high*1.5) out.status = "high";
          else if (out.perKg > sd.high) out.status = "above";
          else out.status = "within";
        }
        return out;
      }

      function interpret(A){
        var box = $("interp");
        var notes = [];
        var T = TARGETS[sp()], I = ins(), s = sp(), sd = I[s];
        var di = doseInfo();
        var freq = parseInt($("freq").value,10);
        var suggestion = null;

        function add(cls, title, body){ notes.push({cls:cls, t:title, b:body}); }

        if (!A.ok){
          box.innerHTML = "<div class='note n-info'><b>Waiting on data</b>Enter at least two sample times with glucose values to generate an interpretation.</div>";
          return null;
        }

        var nadir = A.nadir.mg;
        var speciesWord = s === "dog" ? "dog" : "cat";

        /* --- 1. Nadir --- */
        if (nadir < HYPO_SEVERE){
          add("n-bad","Severe hypoglycaemia on this curve",
            "Nadir " + both(nadir) + " at " + A.nadir.clock + ". This is a dangerous nadir whether or not the patient showed signs. Reduce the dose by 50 percent, or withhold and restart at a lower dose after re-examination. Warn the owner about signs of hypoglycaemia, provide a glucose source at home, and recheck a curve after 5 to 7 days.");
          if (di.dose) suggestion = { dose: Math.max(0.5, round05(di.dose*0.5)), why:"50 percent reduction for a severe hypoglycaemic nadir", dir:"down" };
        } else if (nadir < HYPO){
          add("n-bad","Hypoglycaemia",
            "Nadir " + both(nadir) + " at " + A.nadir.clock + ", below the " + both(HYPO) + " threshold. Reduce the dose by 25 to 50 percent and recheck. Do not increase the dose on this curve regardless of how high the rest of it sits.");
          if (di.dose) suggestion = { dose: Math.max(0.5, round05(di.dose*0.7)), why:"30 percent reduction for a hypoglycaemic nadir", dir:"down" };
        } else if (nadir < T.nadirLow){
          add("n-warn","Nadir below target",
            "Nadir " + both(nadir) + " at " + A.nadir.clock + ", under the target of " + both(T.nadirLow) + " to " + both(T.nadirHigh) + ". This is close enough to trouble to act on. Reduce the dose by 10 to 25 percent, particularly if the owner reports lethargy, wobbliness, hunger or twitching around the expected nadir.");
          if (di.dose) suggestion = { dose: Math.max(0.5, round05(di.dose*0.85)), why:"15 percent reduction for a low but non-hypoglycaemic nadir", dir:"down" };
        } else if (nadir <= T.nadirHigh){
          add("n-good","Nadir in target",
            "Nadir " + both(nadir) + " at " + A.nadir.clock + ", inside the target of " + both(T.nadirLow) + " to " + both(T.nadirHigh) + " for a " + speciesWord + ". The depth of insulin effect is appropriate. Judge the rest of the curve on duration and on the peak.");
        } else {
          add("n-warn","Nadir above target",
            "Nadir " + both(nadir) + " at " + A.nadir.clock + ", above the target ceiling of " + both(T.nadirHigh) + ". The insulin is not dropping the glucose far enough. Before increasing the dose, work through duration of effect, injection technique, product handling and concurrent disease below.");
        }

        /* --- 2. Rebound --- */
        if (A.rebound){
          add("n-bad","Pattern consistent with insulin-induced hyperglycaemia (Somogyi)",
            "Glucose fell to " + both(nadir) + " at " + A.nadir.clock + " then rose to " + both(A.postMax.mg) + " by " + A.postMax.clock + ". That is a documented low followed by a rapid rebound. Reduce the dose, do not increase it. AAHA notes this pattern is over-diagnosed, so confirm with a repeat curve or continuous interstitial glucose monitoring before making it the working diagnosis.");
          if (di.dose && (!suggestion || suggestion.dir !== "down")){
            suggestion = { dose: Math.max(0.5, round05(di.dose*0.75)), why:"25 percent reduction for a documented rebound pattern", dir:"down" };
          }
        }

        /* --- 3. Duration --- */
        if (A.durMode === "none"){
          add("n-warn","Duration of effect cannot be measured",
            "Glucose never fell below " + both(T.ret) + " at any point, so there is no measurable duration of insulin effect on this curve. This is a depth of effect problem, not a duration problem. Work through the dose, the injection technique and the resistance list before drawing any conclusion about how long the insulin lasts.");
        } else if (A.duration !== undefined){
          var dh = A.duration/60;
          var expect = sd && sd.dur ? sd.dur : [10,14];
          var durTxt = (A.durationCensored ? "at least " : "") + dh.toFixed(1) + " hours";
          if (A.durMode === "unreliable"){
            add("n-info","Duration reading is unreliable on this curve",
              "The apparent duration is " + durTxt + ", but the nadir never reached the target range, so the glucose only dipped under " + both(T.ret) + " briefly. That makes the duration look shorter than it is. Correct the depth of effect first, then judge duration on the repeat curve.");
          } else if (A.durationCensored && A.span >= 660 && A.peak.mg <= T.dayHigh){
            add("n-good","Effect covered the whole sampled period",
              "Glucose stayed below " + both(T.ret) + " for the full " + hrs(A.span) + " hours sampled, so the insulin covered the dosing interval. Duration is at least " + dh.toFixed(1) + " hours.");
          } else if (A.durationCensored){
            add("n-info","Duration of effect not yet complete",
              "Glucose had not returned above " + both(T.ret) + " by the last sample, so the duration of effect is " + durTxt + " and may be longer. If you need a true duration, sample until the glucose climbs back or use interstitial monitoring.");
          } else if (freq === 12 && dh < 8){
            add("n-bad","Short duration of insulin effect",
              "Effect lasted " + durTxt + " on twice daily dosing. The patient spends hours of every cycle unmedicated, which drives the polyuria and weight loss the owner is seeing. Raising the dose deepens the nadir without fixing the gap. The correct move is a longer acting insulin. Typical duration for " + esc(I.name.split(" (")[0]) + " in a " + speciesWord + " is " + expect[0] + " to " + expect[1] + " hours.");
          } else if (freq === 12 && dh < 10){
            add("n-warn","Duration slightly short",
              "Effect lasted " + durTxt + " against an ideal of 10 to 14 hours on q12h dosing. Acceptable if the clinical signs are controlled. If they are not, change insulin rather than escalating the dose.");
          } else if (freq === 12 && dh > 14){
            add("n-warn","Duration longer than the dosing interval",
              "Effect lasted " + durTxt + " on a 12 hour interval, so doses are overlapping and stacking. Watch for a progressively deeper nadir over successive days. Consider a shorter acting insulin, a longer interval or a dose reduction.");
          } else if (freq === 24 && dh < 20){
            add("n-warn","Once daily dosing is not covering the day",
              "Effect lasted " + durTxt + " on once daily dosing. Very few diabetic dogs or cats are adequately controlled once daily. Twice daily dosing is the standard of care in both species.");
          } else {
            add("n-good","Duration of effect appropriate",
              "Effect lasted " + durTxt + ", which matches the " + (freq===12?"12":"24") + " hour dosing interval well.");
          }
        }

        /* --- 4. Peak, range and glucose differential --- */
        if (A.peak.mg > T.dayHigh){
          add("n-warn","Peak above the acceptable daily range",
            "Peak " + both(A.peak.mg) + " at " + A.peak.clock + " against an acceptable ceiling of " + both(T.dayHigh) + " for a " + speciesWord + ". Time above range on this curve was " + A.pctAbove.toFixed(0) + " percent. Expect ongoing glucosuria, polyuria and polydipsia while the curve sits here.");
        }
        if (A.diff < 55 && A.mean > T.dayHigh){
          add("n-bad","Flat, high curve with minimal insulin effect",
            "The glucose only moved " + fmt(A.diff) + " " + unitLabel() + " across the whole curve while sitting high. Before touching the dose, work through the list that actually explains most of these: wrong syringe for the insulin concentration, insulin expired or stored badly or shaken to froth, injection into the same fibrotic site repeatedly, injection into hair rather than skin or straight back out, and in cats, stress hyperglycaemia blunting the whole curve. Then consider insulin resistance from concurrent disease.");
        }
        if (A.mean > 0){
          add("n-info","Time in range",
            "Across the sampled period the patient sat inside the target range of " + both(T.dayLow) + " to " + both(T.dayHigh) + " for " + A.pctIn.toFixed(0) + " percent of the time, above it for " + A.pctAbove.toFixed(0) + " percent and below it for " + A.pctBelow.toFixed(0) + " percent. Mean glucose was " + both(A.mean) + ".");
        }

        /* --- 5. Dose context and resistance --- */
        if (di.perKg !== null){
          var perKgTxt = di.perKg.toFixed(2) + " U/kg per injection";
          if (nadir > T.nadirHigh && (di.status === "high" || di.perKg >= 1.5)){
            add("n-bad","Possible insulin resistance",
              "The current dose is " + perKgTxt + ", which is well above the usual range for " + esc(I.name.split(" (")[0]) + " in a " + speciesWord + ", and control is still poor. At this point the problem is usually not the number of units. Investigate hyperadrenocorticism, dioestrus or an intact bitch, acromegaly in cats, obesity, pancreatitis, urinary tract infection, dental or other occult infection, hypothyroidism, renal disease and any exogenous glucocorticoid or progestagen. Increasing the dose further without a workup is where these cases go wrong.");
          } else if ((di.status === "above" || di.status === "high") && nadir >= T.nadirLow && nadir <= T.nadirHigh){
            add("n-info","Dose above the usual starting range",
              "Current dose is " + perKgTxt + " against a usual range of " + sd.low + " to " + sd.high + " U/kg for this product in a " + speciesWord + ". The nadir is not high, so the dose is doing its job. Note it in the record, keep the hypoglycaemia advice in front of the owner, and do not escalate further without a clear reason.");
          } else if (di.status === "above" && nadir > T.nadirHigh){
            add("n-warn","Dose above the usual starting range",
              "Current dose is " + perKgTxt + " against a usual range of " + sd.low + " to " + sd.high + " U/kg for this product in a " + speciesWord + ". Not automatically wrong, but escalate in small steps and rule out resistance before going further.");
          } else if (di.status === "below" && nadir > T.nadirHigh){
            add("n-info","Dose below the usual starting range",
              "Current dose is " + perKgTxt + " against a usual range of " + sd.low + " to " + sd.high + " U/kg for this product in a " + speciesWord + ", and the nadir is still high. There is room to titrate upward.");
          }
          if (I.id === "detemir" && s === "dog" && di.perKg > 0.2){
            add("n-bad","Detemir dose check",
              "Detemir is markedly more potent in dogs than other insulins. At " + perKgTxt + " this dose is high for the product. Confirm the dose against a current reference before continuing.");
          }
        }

        /* --- 6. Dose suggestion for an inadequate curve --- */
        if (!suggestion && nadir > T.nadirHigh && !A.rebound && di.dose){
          // a short apparent duration only blocks an increase when the insulin actually reached target depth
          var canIncrease = !(A.durMode === "ok" && !A.durationCensored && freq === 12 && A.duration/60 < 8);
          if (canIncrease && !(A.diff < 55 && A.mean > T.dayHigh) && di.status !== "high"){
            var pct = nadir > T.dayHigh*1.4 ? 0.25 : 0.15;
            suggestion = { dose: round05(di.dose*(1+pct)), why: "target increase of about " + Math.round(pct*100) + " percent for a nadir above target, rounded to the nearest half unit", dir:"up" };
          }
        }
        if (!suggestion && nadir >= T.nadirLow && nadir <= T.nadirHigh && A.peak.mg <= T.dayHigh){
          add("n-good","No dose change indicated by this curve",
            "Nadir, peak and duration all sit where you want them. Hold the current dose, confirm the clinical picture with the owner, and recheck with a fructosamine or a repeat curve in 4 to 12 weeks depending on stability.");
        }

        /* --- 7. Data quality --- */
        var q = [];
        if (A.n < 6) q.push("only " + A.n + " samples were entered, and AAHA recommends at least 7 over 12 hours");
        if (A.maxGap > 150) q.push("the largest gap between samples was " + hrs(A.maxGap) + " hours, which can step straight over the nadir");
        if (A.injMin !== undefined && Math.abs(A.first.t - A.injMin) > 45) q.push("the first sample was " + hrs(Math.abs(A.first.t - A.injMin)) + " hours away from the injection, so there is no true baseline");
        if (A.span < 600) q.push("the curve only covers " + hrs(A.span) + " hours, which is short of the 12 hours needed to judge duration");
        if (A.stillFalling) q.push("glucose was still falling at the last sample, so the true nadir is probably lower and later than shown");
        if (q.length){
          add("n-warn","Data quality", "Read this curve with caution because " + q.join("; ") + ".");
        }

        /* --- 8. Species and product specific --- */
        if (s === "cat"){
          add("n-info","Cat specific",
            "In-clinic curves in cats are frequently distorted by stress hyperglycaemia and by day to day variability. Home sampling from the ear or paw pad, or continuous interstitial glucose monitoring, gives a far more usable picture. Where remission is the goal, a low carbohydrate diet plus early tight control with a long acting insulin gives the best odds, and remission risk means these patients need close monitoring for hypoglycaemia as they improve.");
        }
        if (sd && sd.poor){
          add("n-warn","Product choice",
            esc(I.name.split(" (")[0]) + " is a poor fit for routine maintenance in a " + speciesWord + ". Fixing the insulin choice will usually do more than any dose adjustment.");
        }

        /* --- render --- */
        var html = "";
        if (suggestion && di.dose){
          var newDose = suggestion.dose;
          var pctChange = Math.round((newDose - di.dose)/di.dose*100);
          var perKgNew = di.w ? (newDose/di.w).toFixed(2) + " U/kg" : "";
          html += "<div class='note " + (suggestion.dir === "down" ? "n-bad" : "n-info") + "' id='sugbox'>" +
            "<b>Suggested dose adjustment</b>" +
            "<div style='font-size:21px;font-weight:800;margin:6px 0'>" + di.dose + " U &rarr; " + newDose + " U per injection" +
            " <span class='badge " + (suggestion.dir==="down"?"b-bad":"b-info") + "' style='vertical-align:middle'>" + (pctChange>0?"+":"") + pctChange + "%</span></div>" +
            (perKgNew ? "<div style='font-size:13px;margin-bottom:6px'>" + perKgNew + " per injection, " + insName().split(" (")[0] + ", U-" + insConc() + " syringes</div>" : "") +
            esc(suggestion.why.charAt(0).toUpperCase() + suggestion.why.slice(1)) + ". Confirm against the clinical picture before changing anything, use the nearest mark your syringe can actually deliver, and recheck after 5 to 7 days on the new dose." +
            "</div>";
        }
        notes.forEach(function(n){
          html += "<div class='note " + n.cls + "'><b>" + n.t + "</b>" + n.b + "</div>";
        });
        box.innerHTML = html;
        return suggestion;
      }

      /* ============================ TILES ============================ */

      function drawTiles(A){
        var box = $("tiles");
        if (!A.ok){
          box.innerHTML = "<div class='tile' style='grid-column:1/-1;background:#F7F6FA'><div class='k'>No curve yet</div><div class='s' style='margin-top:4px'>Enter at least two sample times with glucose values.</div></div>";
          return;
        }
        var T = TARGETS[sp()];
        var nadirCls = A.nadir.mg < HYPO ? "alert" : (A.nadir.mg >= T.nadirLow && A.nadir.mg <= T.nadirHigh ? "ok" : "");
        var u = unitLabel();
        var durTxt = "—", durSub = "";
        if (A.durMode === "none"){
          durTxt = "Not measurable";
          durSub = "Never fell below " + fmt(T.ret) + " " + u;
        } else if (A.duration !== undefined){
          durTxt = (A.durationCensored ? "≥" : "") + (A.duration/60).toFixed(1) + " h";
          durSub = A.durationCensored
            ? "Still below " + fmt(T.ret) + " " + u + " at last sample"
            : "Injection to " + fmt(T.ret) + " " + u;
          if (A.durMode === "unreliable") durSub += ". Nadir above target, read with caution";
        }
        var tiles = [
          {k:"Nadir", v: fmt(A.nadir.mg) + " " + u, s:"at " + A.nadir.clock + (A.toNadir!==undefined? ", " + hrs(A.toNadir) + " h post-insulin":""), c:nadirCls},
          {k:"Peak", v: fmt(A.peak.mg) + " " + u, s:"at " + A.peak.clock, c: A.peak.mg > T.dayHigh ? "" : "ok"},
          {k:"Duration of effect", v: durTxt, s: durSub, c:""},
          {k:"Time in range", v: A.pctIn.toFixed(0) + "%", s:fmt(T.dayLow) + " to " + fmt(T.dayHigh) + " " + u, c: A.pctIn >= 70 ? "ok" : ""},
          {k:"Glucose differential", v: fmt(A.diff) + " " + u, s:"Peak minus nadir", c:""},
          {k:"Mean glucose", v: fmt(A.mean) + " " + u, s:"Across " + hrs(A.span) + " h sampled", c:""},
          {k:"Time above range", v: A.pctAbove.toFixed(0) + "%", s:"Above " + fmt(T.dayHigh) + " " + u, c:""},
          {k:"Time below range", v: A.pctBelow.toFixed(0) + "%", s:"Below " + fmt(T.dayLow) + " " + u + (A.nadir.mg < HYPO ? ". Hypoglycaemia recorded" : ""), c: A.nadir.mg < HYPO ? "alert" : ""}
        ];
        box.innerHTML = tiles.map(function(t){
          return "<div class='tile " + t.c + "'><div class='k'>" + t.k + "</div><div class='v'>" + t.v + "</div><div class='s'>" + t.s + "</div></div>";
        }).join("");
      }

      /* ============================ SUMMARY ============================ */

      function buildSummary(A, suggestion){
        var u = unitLabel(), T = TARGETS[sp()], di = doseInfo();
        var L = [];
        L.push("BLOOD GLUCOSE CURVE");
        L.push("Patient: " + ($("pname").value || "not recorded") + "   Species: " + (sp()==="dog"?"Dog":"Cat") + "   Weight: " + (weightKg() ? weightKg().toFixed(1) + " kg" : "not recorded"));
        L.push("Insulin: " + insName() + "   Dose: " + ($("dose").value || "?") + " units " + ($("freq").value === "12" ? "q12h" : "q24h") + (di.perKg !== null ? " (" + di.perKg.toFixed(2) + " U/kg)" : ""));
        L.push("Insulin given " + $("inj1").value + (($("inj2").value)?" and "+$("inj2").value:"") + ". Fed " + $("feed1").value + (($("feed2").value)?" and "+$("feed2").value:"") + ".");
        L.push("Curve " + $("gstart").value + " to " + $("gend").value + ". Units " + u + ".");
        L.push("");
        L.push("SAMPLES");
        var pts = A.pts || [];
        pts.forEach(function(p){
          var post = (A.injMin !== undefined && A.injMin !== null) ? "  (" + hrs(p.t - A.injMin) + " h post-insulin)" : "";
          L.push("  " + p.clock + "   " + fmt(p.mg) + " " + u + post);
        });
        if (!pts.length) L.push("  none entered");
        L.push("");
        if (A.ok){
          L.push("RESULTS");
          L.push("  Nadir: " + fmt(A.nadir.mg) + " " + u + " at " + A.nadir.clock + (A.toNadir!==undefined ? " (" + hrs(A.toNadir) + " h post-insulin)" : ""));
          L.push("  Peak: " + fmt(A.peak.mg) + " " + u + " at " + A.peak.clock);
          L.push("  Glucose differential: " + fmt(A.diff) + " " + u);
          L.push("  Mean glucose: " + fmt(A.mean) + " " + u + " over " + hrs(A.span) + " h");
          if (A.durMode === "none") L.push("  Duration of insulin effect: not measurable, glucose never fell below " + fmt(T.ret) + " " + u);
          else if (A.duration !== undefined) L.push("  Duration of insulin effect: " + (A.durationCensored?"at least ":"") + (A.duration/60).toFixed(1) + " h" + (A.durMode === "unreliable" ? " (apparent only, nadir did not reach target)" : ""));
          L.push("  Time in target (" + fmt(T.dayLow) + " to " + fmt(T.dayHigh) + " " + u + "): " + A.pctIn.toFixed(0) + "%  |  above " + A.pctAbove.toFixed(0) + "%  |  below " + A.pctBelow.toFixed(0) + "%");
          L.push("");
          L.push("ASSESSMENT");
          root.querySelectorAll("#interp .note").forEach(function(n){
            if (n.id === "sugbox") return;
            var b = n.querySelector("b");
            if (b) L.push("  - " + b.textContent);
          });
          L.push("");
          L.push("PLAN");
          if (suggestion) L.push("  Adjust insulin from " + di.dose + " U to " + suggestion.dose + " U per injection. " + suggestion.why.charAt(0).toUpperCase() + suggestion.why.slice(1) + ". Recheck in 5 to 7 days.");
          else L.push("  ");
        }
        L.push("");
        L.push("Interpreted against the 2018 AAHA Diabetes Management Guidelines for Dogs and Cats.");
        $("summary").value = L.join("\n");
      }

      /* ============================ RECALC ============================ */

      function updateInsulinUI(){
        var I = ins(), s = sp(), sd = I[s], conc = insConc();
        $("customwrap").style.display = I.id === "other" ? "block" : "none";

        var cls = conc === 40 ? "b-warn" : (conc === 300 ? "b-bad" : "b-info");
        var msg = "U-" + conc + " insulin. Use U-" + conc + " syringes only.";
        if (conc === 300) msg = "U-300 insulin. Extremely concentrated. Verify the syringe and the volume before every dose.";
        $("concbadge").innerHTML = "<span class='badge " + cls + "'>" + msg + "</span>";

        var w = weightKg(), d = parseFloat($("dose").value);
        var out = "";
        if (!isNaN(d) && w){
          var pk = d / w;
          out = pk.toFixed(2) + " U/kg per injection";
          if (sd && sd.low !== null){
            out += " (usual start " + sd.low + " to " + sd.high + " U/kg for a " + (s==="dog"?"dog":"cat") + ")";
          }
          if (I.id !== "other"){
            var vol = d / conc;
            out += ". Volume drawn " + vol.toFixed(2) + " mL.";
          }
        } else if (!w){
          out = "Enter a body weight to see the dose per kilogram.";
        }
        $("dosekg").textContent = out;

        var n = "<div class='note n-info'><b>" + esc(I.name) + "</b>" + esc(I.note) +
          (sd && sd.flat ? " " + esc(sd.flat) + "." : "") + "</div>";
        $("insulinnote").innerHTML = n;
      }

      function recalc(){
        root.querySelectorAll(".uLabel").forEach(function(e){ e.textContent = unitLabel(); });
        updateInsulinUI();
        var A = analyse();
        drawChart(A);
        drawTiles(A);
        var sug = interpret(A);
        buildSummary(A, sug);
      }

      /* ============================ EVENTS ============================ */

      function init(){
        buildInsulinSelect();
        buildRefTable();
        for (var i=0;i<7;i++) rows.push({time:m2t(8*60 + i*120), bg:""});
        renderRows();

        ["pname","species","weight","wunit","insulin","dose","freq","inj1","inj2","feed1","feed2","gstart","gend","customname","customconc"]
          .forEach(function(id){
            $(id).addEventListener("input", function(){
              if (id === "freq" || id === "inj1"){
                var im = t2m($("inj1").value);
                if (im !== null) $("inj2").value = m2t(im + parseInt($("freq").value,10)*60);
              }
              if (id === "gstart" || id === "gend") renderRows();
              recalc();
            });
            $(id).addEventListener("change", recalc);
          });

        $("unitseg").addEventListener("click", function(e){
          var b = e.target.closest("button");
          if (!b) return;
          var nu = b.dataset.u;
          if (nu === unit) return;
          rows.forEach(function(r){
            var v = parseFloat(r.bg);
            if (isNaN(v)) return;
            r.bg = nu === "mgdl" ? String(Math.round(v * MGDL_PER_MMOL)) : (v / MGDL_PER_MMOL).toFixed(1);
          });
          unit = nu;
          this.querySelectorAll("button").forEach(function(x){ x.classList.toggle("on", x.dataset.u === nu); });
          root.querySelectorAll("#samples input[data-f=bg]").forEach(function(el){
            el.step = nu === "mmol" ? "0.1" : "1";
          });
          renderRows(); recalc();
        });

        $("addrow").addEventListener("click", function(){
          var last = rows.length ? t2m(rows[rows.length-1].time) : t2m($("gstart").value);
          rows.push({ time: last !== null ? m2t(last + 120) : "", bg:"" });
          renderRows(); recalc();
        });
        $("fill2").addEventListener("click", function(){ fillTimes(2); });
        $("fill1").addEventListener("click", function(){ fillTimes(1); });
        $("clearvals").addEventListener("click", function(){
          rows.forEach(function(r){ r.bg = ""; });
          renderRows(); recalc();
        });
        $("resetall").addEventListener("click", function(){
          if (!confirm("Clear the whole form?")) return;
          $("pname").value = "";
          $("species").value = "dog";
          $("weight").value = "";
          $("wunit").value = "kg";
          $("insulin").value = "vetsulin";
          $("dose").value = "";
          $("freq").value = "12";
          $("inj1").value = "08:00";
          $("inj2").value = "20:00";
          $("feed1").value = "08:00";
          $("feed2").value = "20:00";
          $("gstart").value = "08:00";
          $("gend").value = "20:00";
          $("customname").value = "";
          $("customconc").value = "100";
          rows = [];
          for (var i = 0; i < 7; i++) {
            rows.push({ time: m2t(8 * 60 + i * 120), bg: "" });
          }
          unit = "mmol";
          $("unitseg").querySelectorAll("button").forEach(function(button) {
            button.classList.toggle("on", button.dataset.u === "mmol");
          });
          renderRows();
          recalc();
        });

        $("loadeg").addEventListener("click", function(){
          $("pname").value = "Example - short duration";
          $("species").value = "dog";
          $("weight").value = "22"; $("wunit").value = "kg";
          $("insulin").value = "nph";
          $("dose").value = "9"; $("freq").value = "12";
          $("inj1").value = "08:00"; $("inj2").value = "20:00";
          $("feed1").value = "08:00"; $("feed2").value = "20:00";
          $("gstart").value = "08:00"; $("gend").value = "20:00";
          var mgdl = [22.0, 12.5, 6.8, 7.5, 14.5, 19.5, 22.5];  // mmol/L
          rows = mgdl.map(function(v, i){
            return { time: m2t(8*60 + i*120), bg: unit === "mmol" ? v.toFixed(1) : String(Math.round(v*MGDL_PER_MMOL)) };
          });
          renderRows(); recalc();
          root.querySelector("#chart").scrollIntoView({behavior:"smooth", block:"center"});
        });

        $("copybtn").addEventListener("click", function(){
          var ta = $("summary");
          ta.select(); ta.setSelectionRange(0, 999999);
          try { document.execCommand("copy"); this.textContent = "Copied"; } catch(e){ this.textContent = "Select and copy"; }
          var b = this;
          setTimeout(function(){ b.textContent = "Copy summary"; }, 1800);
        });

        recalc();
      }

      init();
    }
  }

  if (!customElements.get("s-glucose-curve-calculator")) {
    customElements.define("s-glucose-curve-calculator", GlucoseCurveCalculator);
  }
})();
